<template>
  <v-container fluid class="legal-page pa-0">
    <section class="legal-hero">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="10" lg="9">
            <v-card class="legal-card">
              <div class="text-center mb-8">
                <v-img
                  :src="require('@/assets/logo.png')"
                  contain
                  height="90"
                  class="mx-auto mb-4"
                />
                <h1>{{ $t('info.cookiesTitle') }} - Konvenio</h1>
                <p class="legal-meta">
                  Fecha de entrada en vigor: 4 de mayo de 2026 | Última actualización: 4 de mayo de 2026 | Versión: v{{ legalTermsVersion }}
                </p>
              </div>

              <div class="legal-text">
                <section>
                  <h2>1. Introducción</h2>
                  <p>
                    En Konvenio, accesible desde
                    <a href="https://konvenio.thevalkiria.com/" target="_blank" rel="noopener">
                      https://konvenio.thevalkiria.com/
                    </a>,
                    utilizamos cookies, almacenamiento local y tecnologías similares para garantizar el funcionamiento
                    del servicio, mantener la seguridad, gestionar la autenticación, recordar preferencias y, cuando
                    corresponda, analizar el uso del sitio web y la aplicación.
                  </p>
                  <p>
                    Esta Política de Cookies explica qué son las cookies, qué tipos de tecnologías podemos utilizar,
                    con qué finalidad se usan y cómo puedes gestionar tus preferencias. Esta política debe leerse junto
                    con nuestra
                    <router-link to="/privacy-policy" target="_blank" rel="noopener">Política de Privacidad</router-link>.
                  </p>
                </section>

                <section>
                  <h2>2. ¿Qué son las cookies?</h2>
                  <p>
                    Las cookies son pequeños archivos que se descargan o almacenan en el dispositivo del usuario al
                    acceder a un sitio web. Permiten, entre otras cosas, almacenar y recuperar información sobre la
                    navegación, el dispositivo, la sesión del usuario, sus preferencias o sus interacciones con el
                    servicio.
                  </p>
                  <p>
                    Además de cookies, Konvenio puede utilizar tecnologías similares, como almacenamiento local del
                    navegador, identificadores técnicos o mecanismos equivalentes necesarios para el funcionamiento del
                    servicio. En esta política, usamos el término "cookies" para referirnos también a estas tecnologías
                    similares.
                  </p>
                </section>

                <section>
                  <h2>3. Responsable del uso de cookies</h2>
                  <ul>
                    <li><strong>Responsable:</strong> Sorraylla Ramos</li>
                    <li><strong>Correo electrónico:</strong> valkiria.lab.apps@gmail.com</li>
                    <li><strong>Sitio web:</strong> https://konvenio.thevalkiria.com/</li>
                    <li><strong>País:</strong> España</li>
                  </ul>
                </section>

                <section>
                  <h2>4. Tipos de cookies y tecnologías utilizadas</h2>

                  <h3>4.1. Cookies técnicas o necesarias</h3>
                  <p>
                    Son necesarias para que el sitio web, la aplicación y sus funciones básicas puedan operar
                    correctamente. Permiten gestionar el inicio de sesión, mantener la sesión activa, proteger el acceso
                    a la cuenta, recordar configuraciones técnicas esenciales y garantizar la seguridad del servicio.
                  </p>
                  <ul>
                    <li>Tokens de autenticación.</li>
                    <li>Identificadores técnicos de sesión.</li>
                    <li>Almacenamiento local necesario para mantener la sesión.</li>
                    <li>Preferencias técnicas imprescindibles.</li>
                    <li>Elementos de seguridad asociados a Firebase Authentication.</li>
                  </ul>
                  <p>
                    Estas cookies son necesarias para prestar el servicio solicitado por el usuario y no requieren
                    consentimiento previo. Si se bloquean desde el navegador, algunas funciones de Konvenio podrían no
                    funcionar correctamente.
                  </p>

                  <h3>4.2. Cookies de preferencias</h3>
                  <p>
                    Permiten recordar ciertos ajustes básicos del usuario para mejorar su experiencia dentro del
                    servicio.
                  </p>
                  <ul>
                    <li>Idioma seleccionado.</li>
                    <li>Configuración visual.</li>
                    <li>Preferencias simples de uso.</li>
                    <li>Ajustes básicos de la interfaz.</li>
                  </ul>
                  <p>
                    Cuando la normativa aplicable lo exija, se solicitará el consentimiento del usuario antes de
                    utilizarlas.
                  </p>

                  <h3>4.3. Cookies analíticas o de medición</h3>
                  <p>
                    Konvenio puede utilizar servicios de análisis proporcionados por Google Firebase o Google Analytics
                    para obtener información agregada sobre el uso del servicio.
                  </p>
                  <ul>
                    <li>Entender cómo se utiliza Konvenio.</li>
                    <li>Medir interacciones dentro de la aplicación o sitio web.</li>
                    <li>Detectar errores o problemas de funcionamiento.</li>
                    <li>Mejorar funciones.</li>
                    <li>Optimizar la experiencia de usuario.</li>
                    <li>Analizar métricas agregadas de uso.</li>
                  </ul>
                  <p>
                    Los datos tratados pueden incluir tipo de dispositivo, sistema operativo, navegador, idioma, eventos
                    de uso, interacciones dentro de la aplicación, métricas agregadas y datos técnicos similares.
                  </p>
                  <p>
                    Las cookies o tecnologías analíticas no esenciales solo se utilizarán cuando sea legalmente aplicable
                    y, cuando sea necesario, tras obtener el consentimiento previo del usuario.
                  </p>
                </section>

                <section>
                  <h2>5. Cookies de terceros</h2>
                  <p>
                    Konvenio utiliza servicios proporcionados por Google, en particular Firebase y, en su caso, Google
                    Analytics. Estos servicios pueden utilizar cookies, identificadores técnicos, almacenamiento local u
                    otras tecnologías similares gestionadas por Google.
                  </p>
                  <ul>
                    <li><strong>Firebase Hosting:</strong> alojamiento del sitio web y entrega segura del contenido.</li>
                    <li><strong>Firebase Authentication:</strong> gestión del inicio de sesión mediante Google, email y contraseña.</li>
                    <li><strong>Cloud Firestore:</strong> almacenamiento de datos necesarios para el servicio.</li>
                    <li><strong>Firebase Analytics:</strong> medición agregada del uso del servicio.</li>
                    <li><strong>Cloud Functions:</strong> ejecución de funciones de backend.</li>
                    <li><strong>Firebase Cloud Messaging:</strong> envío de notificaciones push.</li>
                  </ul>
                  <p>
                    Google puede tratar datos en servidores ubicados fuera del Espacio Económico Europeo. Cuando sea
                    aplicable, estas transferencias se realizan conforme a mecanismos legalmente reconocidos, como
                    cláusulas contractuales tipo, decisiones de adecuación u otras garantías previstas por la normativa de
                    protección de datos.
                  </p>
                  <p>
                    Más información sobre Google:
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener">
                      https://policies.google.com/privacy
                    </a>
                  </p>
                </section>

                <section>
                  <h2>6. Base jurídica para el uso de cookies</h2>
                  <v-simple-table class="legal-table">
                    <thead>
                      <tr>
                        <th>Categoría</th>
                        <th>Finalidad</th>
                        <th>Base jurídica / consentimiento</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Cookies técnicas o necesarias</td>
                        <td>Prestar el servicio solicitado y permitir el funcionamiento básico de Konvenio.</td>
                        <td>No requieren consentimiento previo.</td>
                      </tr>
                      <tr>
                        <td>Cookies de preferencias</td>
                        <td>Recordar ajustes básicos o personalizar la experiencia.</td>
                        <td>Consentimiento cuando no sean estrictamente necesarias.</td>
                      </tr>
                      <tr>
                        <td>Cookies analíticas o de medición</td>
                        <td>Medir el uso del servicio y mejorar su funcionamiento.</td>
                        <td>Consentimiento cuando así lo exija la normativa aplicable.</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </section>

                <section>
                  <h2>7. Gestión del consentimiento</h2>
                  <p>
                    Al acceder al sitio web, el usuario podrá aceptar, rechazar o configurar el uso de cookies no
                    necesarias mediante el banner o panel de configuración de cookies, cuando esté disponible.
                  </p>
                  <ul>
                    <li>Aceptar todas las cookies.</li>
                    <li>Rechazar las cookies no necesarias.</li>
                    <li>Configurar sus preferencias por categorías.</li>
                    <li>Retirar o modificar su consentimiento posteriormente.</li>
                  </ul>
                  <p>
                    Las cookies técnicas o necesarias no pueden desactivarse desde el panel de consentimiento, ya que son
                    imprescindibles para el funcionamiento del servicio. No obstante, el usuario puede bloquearlas desde
                    la configuración de su navegador, aunque esto puede afectar al correcto funcionamiento de Konvenio.
                  </p>
                  <v-btn color="primary" outlined @click="openCookieSettings">
                    Configurar cookies
                  </v-btn>
                </section>

                <section>
                  <h2>8. Cómo desactivar o eliminar cookies desde el navegador</h2>
                  <p>
                    El usuario puede permitir, bloquear o eliminar las cookies instaladas en su dispositivo mediante la
                    configuración del navegador que utilice.
                  </p>
                  <ul>
                    <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener">Google Chrome</a></li>
                    <li><a href="https://support.mozilla.org/es/kb/Borrar%20cookies" target="_blank" rel="noopener">Mozilla Firefox</a></li>
                    <li><a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener">Safari</a></li>
                    <li><a href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge" target="_blank" rel="noopener">Microsoft Edge</a></li>
                  </ul>
                  <p>
                    El bloqueo o eliminación de cookies técnicas o necesarias puede impedir el inicio de sesión, afectar
                    a la autenticación o limitar el funcionamiento de documentos, avisos, calendarios, gestión de
                    trabajadores y otras funciones de Konvenio.
                  </p>
                </section>

                <section>
                  <h2>9. Periodo de conservación</h2>
                  <v-simple-table class="legal-table">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Periodo de conservación</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Cookies técnicas o necesarias</td>
                        <td>Durante el tiempo necesario para mantener la sesión, garantizar la seguridad o permitir el funcionamiento del servicio.</td>
                      </tr>
                      <tr>
                        <td>Cookies de preferencias</td>
                        <td>Durante el tiempo necesario para recordar la configuración seleccionada por el usuario o hasta que este las elimine.</td>
                      </tr>
                      <tr>
                        <td>Cookies analíticas</td>
                        <td>Durante el periodo configurado en Firebase/Google Analytics o hasta que el usuario retire su consentimiento, cuando corresponda.</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </section>

                <section>
                  <h2>10. Transferencias internacionales</h2>
                  <p>
                    El uso de servicios de Google Firebase o Google Analytics puede implicar el tratamiento de datos en
                    países situados fuera del Espacio Económico Europeo. Cuando sea aplicable, dichas transferencias se
                    realizarán conforme a garantías adecuadas previstas por la normativa de protección de datos.
                  </p>
                </section>

                <section>
                  <h2>11. Actualización de la Política de Cookies</h2>
                  <p>
                    Konvenio podrá actualizar esta Política de Cookies para adaptarla a cambios legales, técnicos o
                    funcionales, así como a modificaciones en los servicios de terceros utilizados. Cuando se realicen
                    cambios importantes, se actualizará la fecha de "Última actualización" y, si corresponde, se informará
                    al usuario mediante el sitio web, la aplicación o el banner de cookies.
                  </p>
                </section>

                <section>
                  <h2>12. Contacto</h2>
                  <ul>
                    <li><strong>Sitio web:</strong> https://konvenio.thevalkiria.com/</li>
                    <li><strong>Email:</strong> valkiria.lab.apps@gmail.com</li>
                    <li><strong>Responsable:</strong> Sorraylla Ramos</li>
                  </ul>
                </section>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </v-container>
</template>

<script>
import { LEGAL_TERMS_VERSION } from '@/config/legal'

export default {
  name: 'CookiesPolicy',
  computed: {
    legalTermsVersion() {
      return LEGAL_TERMS_VERSION
    }
  },
  methods: {
    openCookieSettings () {
      if (window.KonvenioCookies && window.KonvenioCookies.openSettings) {
        window.KonvenioCookies.openSettings()
      }
    }
  }
}
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: #f7f7fb;
}

.legal-hero {
  min-height: 100vh;
  padding: 48px 0;
  background: linear-gradient(135deg, rgba(123, 47, 247, 0.12), rgba(255, 255, 255, 1));
}

.legal-card {
  border-radius: 10px;
  padding: 32px;
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.16);
}

.legal-card h1 {
  font-size: 32px;
  font-weight: 800;
  line-height: 1.2;
}

.legal-meta {
  color: #5f6368;
  font-size: 14px;
  margin-top: 10px;
}

.legal-text section {
  margin-top: 28px;
}

.legal-text h2 {
  color: #2f2457;
  font-size: 22px;
  font-weight: 800;
  margin-bottom: 12px;
}

.legal-text h3 {
  color: #3b3260;
  font-size: 18px;
  font-weight: 700;
  margin: 22px 0 8px;
}

.legal-text p,
.legal-text li {
  color: #3f3f46;
  line-height: 1.7;
}

.legal-text ul {
  padding-left: 22px;
}

.legal-text a {
  color: #5e35b1;
  font-weight: 700;
  text-decoration: none;
}

.legal-text a:hover {
  text-decoration: underline;
}

.legal-table {
  border: 1px solid rgba(94, 53, 177, 0.14);
  border-radius: 8px;
  overflow: hidden;
}

@media (max-width: 600px) {
  .legal-hero {
    padding: 24px 0;
  }

  .legal-card {
    padding: 22px;
  }

  .legal-card h1 {
    font-size: 26px;
  }
}
</style>
